import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

// Material UI components
import {
  Box,
  Typography,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
  Container,
  Paper,
  AlertColor
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import tenantService from "../../services/TenantService";
import { OrganizationDTO, UserDTO, InvitationDTO } from "../../types/Tenant";

// Import panel components
import OrganizationDetailsPanel from "./OrganizationDetailsPanel";
import OrganizationUsersPanel from "./OrganizationUsersPanel";
import OrganizationSubscriptionPanel from "./OrganizationSubscriptionPanel";

// Default empty organization
const emptyOrganization: OrganizationDTO = {
  id: "",
  name: "",
  street_address: "",
  city: "",
  state: "",
  zip_code: "",
  phone: "",
  website: "",
  mission: "",
  vision: ""
};

const OrganizationProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ id: string }>();
  // Check if organization data was passed through navigation state
  const passedOrganization = location.state?.organization as OrganizationDTO | undefined;
  const expandSubscription = location.state?.expandSubscription as boolean | undefined;
  const subscriptionMessage = location.state?.message as string | undefined;
  
  // Form state for organization details
  const [orgName, setOrgName] = useState<string>("");
  const [orgStreetAddress, setOrgStreetAddress] = useState<string>("");
  const [orgCity, setOrgCity] = useState<string>("");
  const [orgState, setOrgState] = useState<string>("");
  const [orgZipCode, setOrgZipCode] = useState<string>("");
  const [orgPhone, setOrgPhone] = useState<string>("");
  const [orgWebsite, setOrgWebsite] = useState<string>("");
  const [orgMission, setOrgMission] = useState<string>("");
  const [orgVision, setOrgVision] = useState<string>("");

  // Validation errors
  const [nameError] = useState<string>("");
  const [streetAddressError] = useState<string>("");
  const [cityError] = useState<string>("");
  const [stateError] = useState<string>("");
  const [zipCodeError] = useState<string>("");
  const [phoneError] = useState<string>("");
  const [websiteError] = useState<string>("");
  
  // State for organization data
  const [organization, setOrganization] = useState<OrganizationDTO | null>(null);
  
  // UI state
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [snackOpen, setSnackOpen] = useState<boolean>(false);
  const [snackMessage, setSnackMessage] = useState<string>("");
  const [snackSeverity, setSnackSeverity] = useState<AlertColor>("success");
  
  // Track expanded panels
  const [detailsExpanded, setDetailsExpanded] = useState<boolean>(true);
  const [usersExpanded, setUsersExpanded] = useState<boolean>(false);
  const [subscriptionExpanded, setSubscriptionExpanded] = useState<boolean>(false);
  
  // Track if form has been modified
  const [formChanged, setFormChanged] = useState<boolean>(false);
  
  // Organization users and invitations
  const [orgUsers, setOrgUsers] = useState<UserDTO[]>([]);
  const [orgInvitations, setOrgInvitations] = useState<InvitationDTO[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState<boolean>(false);
  
  // Fetch organization data
  const fetchOrganization = useCallback(async () => {
    if (passedOrganization) {
      setOrganization(passedOrganization);
      return;
    }
    
    // If not in path params, check query params for backward compatibility
    const queryParams = new URLSearchParams(location.search);
    const orgId = params.id || queryParams.get('id');
    
    // Store organization ID in localStorage for components that need it
    if (orgId) {
      localStorage.setItem('organizationId', orgId);
    }
    
    if (!orgId) {
      setIsLoading(false);
      return;
    }
    
    try {
      const data = await tenantService.getOrganization(orgId);
      setOrganization(data);
    } catch (error) {
      handleError("Failed to load organization data.");
    } finally {
      setIsLoading(false);
    }
  }, [location.search, passedOrganization, params]);
  
  // Fetch organization users - only called when panel is expanded or when needed
  const fetchOrganizationUsers = useCallback(async () => {
    if (!organization?.id) return;
    
    setIsLoadingUsers(true);
    try {
      const data = await tenantService.getOrganizationUsers(organization.id);
      setOrgUsers(data);
    } catch (error: any) {
      handleError(error.message || 'Failed to load users.');
    } finally {
      setIsLoadingUsers(false);
    }
  }, [organization]);

  // Fetch organization invitations - only called when panel is expanded or when needed
  const fetchOrganizationInvitations = useCallback(async () => {
    if (!organization?.id) return;
    
    setIsLoadingInvitations(true);
    try {
      const data = await tenantService.getOrganizationInvitations(organization.id);
      setOrgInvitations(data);
    } catch (error: any) {
      handleError(error.message || 'Failed to load invitations.');
    } finally {
      setIsLoadingInvitations(false);
    }
  }, [organization]);
  
  // Load users and invitations when organization is loaded
  useEffect(() => {
    if (organization?.id) {
      fetchOrganizationUsers();
      fetchOrganizationInvitations();
    }
  }, [organization?.id, fetchOrganizationUsers, fetchOrganizationInvitations]);

  useEffect(() => {
    if (expandSubscription && !isLoading) {
      setSubscriptionExpanded(true);
      setDetailsExpanded(false);
      setUsersExpanded(false);
      
      if (subscriptionMessage) {
        handleError(subscriptionMessage);
      }
    }
  }, [isLoading, expandSubscription, subscriptionMessage]);

  // Load organization data on component mount and when URL params change
  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  // Initialize component and load data
  useEffect(() => {
    // Don't duplicate the fetchOrganization call, it's already called in the first useEffect
    
    // If organization is passed, initialize form fields
    if (passedOrganization) {
      setOrgName(passedOrganization.name || '');
      setOrgStreetAddress(passedOrganization.street_address || '');
      setOrgCity(passedOrganization.city || '');
      setOrgState(passedOrganization.state || '');
      setOrgZipCode(passedOrganization.zip_code || '');
      setOrgPhone(passedOrganization.phone || '');
      setOrgWebsite(passedOrganization.website || '');
      setOrgMission(passedOrganization.mission || '');
      setOrgVision(passedOrganization.vision || '');
      setIsLoading(false);
    }
    
    // Check if we're returning from Stripe checkout
    const queryParams = new URLSearchParams(location.search);
    const sessionId = queryParams.get('session_id');
    const canceled = queryParams.get('canceled');
    
    // Open the subscription panel if we're returning from Stripe
    if (sessionId || canceled) {
      setDetailsExpanded(false);
      setUsersExpanded(false);
      setSubscriptionExpanded(true);
    } else {
      // Default panel state
      setDetailsExpanded(true);
      setUsersExpanded(false);
      setSubscriptionExpanded(false);
    }
  }, [fetchOrganization, passedOrganization, location.search]);

  // Initialize form fields when organization changes
  useEffect(() => {
    if (organization) {
      setOrgName(organization.name || '');
      setOrgStreetAddress(organization.street_address || '');
      setOrgCity(organization.city || '');
      setOrgState(organization.state || '');
      setOrgZipCode(organization.zip_code || '');
      setOrgPhone(organization.phone || '');
      setOrgWebsite(organization.website || '');
      setOrgMission(organization.mission || '');
      setOrgVision(organization.vision || '');
    }
  }, [organization]);

  // Handle input changes for organization details
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormChanged(true);
    
    switch (name) {
      case "name":
        setOrgName(value);
        break;
      case "street_address":
        setOrgStreetAddress(value);
        break;
      case "city":
        setOrgCity(value);
        break;
      case "state":
        setOrgState(value);
        break;
      case "zip_code":
        setOrgZipCode(value);
        break;
      case "phone":
        setOrgPhone(value);
        break;
      case "website":
        setOrgWebsite(value);
        break;
      case "mission":
        setOrgMission(value);
        break;
      case "vision":
        setOrgVision(value);
        break;
      default:
        break;
    }
  };

  // Save organization data
  const handleSave = async () => {
    // Validate required fields
    if (!orgName.trim()) {
      handleError("Organization name is required");
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Build organization object
      const organizationData: OrganizationDTO = {
        ...(organization || {}),
        id: organization?.id || "",
        name: orgName,
        street_address: orgStreetAddress,
        city: orgCity,
        state: orgState,
        zip_code: orgZipCode,
        phone: orgPhone,
        website: orgWebsite,
        mission: orgMission,
        vision: orgVision
      };
      
      let result;
      if (organization?.id) {
        // Update existing organization
        result = await tenantService.updateOrganization(organizationData);
        handleSuccess("Organization updated successfully");
      } else {
        // Create new organization
        result = await tenantService.createOrganization(organizationData);
        handleSuccess("Organization created successfully");
      }
      
      setOrganization(result);
      setFormChanged(false);
      
      // Navigate back to dashboard after saving
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500); // Short delay to show the success message before redirecting
    } catch (error) {
      handleError("Failed to save organization");
    } finally {
      setIsSaving(false);
    }
  };

  // Handle panels expansion
  const handleDetailsChange = async (event: React.SyntheticEvent, isExpanded: boolean) => {
    // If closing details panel and there are changes, save first
    if (!isExpanded && detailsExpanded && formChanged) {
      await handleSave();
    }
    
    setDetailsExpanded(isExpanded);
    if (isExpanded) {
      setUsersExpanded(false);
      setSubscriptionExpanded(false);
    }
  };
  
  const handleUsersChange = async (event: React.SyntheticEvent, isExpanded: boolean) => {
    // If opening users panel and organization has ID, fetch data
    if (isExpanded && organization?.id) {
      fetchOrganizationUsers();
      fetchOrganizationInvitations();
    }
    
    setUsersExpanded(isExpanded);
    if (isExpanded) {
      setDetailsExpanded(false);
      setSubscriptionExpanded(false);
    }
  };
  
  const handleSubscriptionChange = (event: React.SyntheticEvent, isExpanded: boolean) => {
    // Only load subscription data when panel is expanded
    if (isExpanded && organization?.id) {
      // No explicit data loading needed here currently as the panel
      // doesn't seem to require API calls, but would go here if needed
    }
    
    setSubscriptionExpanded(isExpanded);
    if (isExpanded) {
      setDetailsExpanded(false);
      setUsersExpanded(false);
    }
  };

  // Handle success feedback
  const handleSuccess = (message: string) => {
    setSnackMessage(message);
    setSnackSeverity("success");
    setSnackOpen(true);
  };

  // Handle error feedback
  const handleError = (message: string) => {
    setSnackMessage(message);
    setSnackSeverity("error");
    setSnackOpen(true);
  };

  // Handle snackbar close
  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  // Handle cancel/back button click
  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        {/* Header with action buttons */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">
            {'Organization'}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={isSaving || !formChanged}
            >
              Save
            </Button>
          </Stack>
        </Box>
        
        {/* Panel contents */}
        <OrganizationDetailsPanel
          expanded={detailsExpanded}
          onChange={handleDetailsChange}
          organization={organization}
          orgName={orgName}
          orgAddress={orgStreetAddress}
          orgCity={orgCity}
          orgState={orgState}
          orgZipCode={orgZipCode}
          orgPhone={orgPhone}
          orgWebsite={orgWebsite}
          orgMission={orgMission}
          orgVision={orgVision}
          nameError={nameError}
          addressError={streetAddressError}
          cityError={cityError}
          stateError={stateError}
          zipCodeError={zipCodeError}
          phoneError={phoneError}
          websiteError={websiteError}
          handleInputChange={handleInputChange}
        />
        
        <Box sx={{ mb: 3 }}>
          {subscriptionMessage && !snackOpen && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {subscriptionMessage}
            </Alert>
          )}
          <OrganizationSubscriptionPanel
            organization={organization || emptyOrganization}
            expanded={subscriptionExpanded}
            onChange={handleSubscriptionChange}
          />
        </Box>
        
        <OrganizationUsersPanel
          expanded={usersExpanded}
          onChange={handleUsersChange}
          organization={organization || emptyOrganization}
          orgUsers={orgUsers}
          orgInvitations={orgInvitations}
          isLoadingUsers={isLoadingUsers}
          isLoadingInvitations={isLoadingInvitations}
          onInvitationSent={() => {
            // Refresh invitations data when a new invitation is sent
            if (organization?.id) {
              fetchOrganizationInvitations();
            }
          }}
        />
      </Paper>
      
      {/* Feedback snackbar */}
      <Snackbar 
        open={snackOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackClose} 
          severity={snackSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default OrganizationProfile;
